import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';

import { useToggle } from 'Shared/utils/hooks';
import { getGoalsByComplete } from 'Shared/selectors/Goals.selectors';
import { fetchGoals, updateParentGoal } from 'Shared/actions/Goals.actions';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpIcon from 'Wpds/components/icon/WpIcon';
import ColorConstants from 'Wpds/colors/ColorConstants';

import SubGoalSingleValue from './SubGoalSingleValue/SubGoalSingleValue';
import SubGoalFieldOption from './SubGoalFieldOption/SubGoalFieldOption';
import ConfirmAddSubGoalModal from './ConfirmAddSubGoalModal/ConfirmAddSubGoalModal';

import './SubGoalField.scss';

type Props = {
  goalId: number;
  onActiveToggle: Function;
};

const styles = {
  menuPortal: base => ({ ...base, zIndex: '1050' }),
  container: base => ({
    ...base,
    width: '100%',
    maxWidth: '300px',
    display: 'flex',
  }),
  control: base => ({ ...base, width: '100%' }),
  indicatorsContainer: base => ({ ...base, padding: '0 0.5rem', color: ColorConstants.gray[500] }),
};

const initConfirmModalConfig = {
  isOpen: false,
  goalSlug: null,
};

function SubGoalField({ goalId, onActiveToggle }: Props) {
  const dispatch = useDispatch();
  const [confirmAddSubGoalModal, setConfirmAddSubGoalModal] = useState(initConfirmModalConfig);

  const [isLoading, toggleIsLoading] = useToggle(false);
  const [option, setOption] = useState(null);

  const [isFetching, toggleIsFetching] = useToggle(false);
  useEffect(() => {
    async function fetchData() {
      toggleIsFetching();
      await dispatch(fetchGoals({ context: 'team' }));
      toggleIsFetching();
    }

    fetchData();
  }, []);

  const goals = useSelector(state =>
    getGoalsByComplete(state, { complete: false })
      .valueSeq()
      .toJS()
      .filter(({ id, parentId, childrensCount }) => id !== goalId && parentId !== goalId && childrensCount === 0)
  );

  const options = goals.map(({ name, id, slug, assigneeId, icon, parentId, workspaceSlug, privacySetting }) => ({
    label: name,
    value: id,
    slug,
    assigneeId,
    icon,
    parentId,
    workspaceSlug,
    privacySetting,
  }));

  const handleSelectChange = opt => setOption(opt);

  const handleCancelClick = () => onActiveToggle();

  const handleConfirmModalSubmit = useCallback(() => {
    dispatch(updateParentGoal({ goal: { slug: confirmAddSubGoalModal.goalSlug, parentId: goalId } })).then(() =>
      onActiveToggle()
    );
  }, [goalId, onActiveToggle, dispatch, confirmAddSubGoalModal.goalSlug]);

  const handleConfirmModalClose = useCallback(() => {
    setConfirmAddSubGoalModal(initConfirmModalConfig);
  }, []);

  const handleSaveClick = () => {
    if (option.parentId) {
      return setConfirmAddSubGoalModal({
        isOpen: true,
        goalSlug: option.slug,
      });
    }
    toggleIsLoading();
    return dispatch(updateParentGoal({ goal: { slug: option.slug, parentId: goalId } })).then(() => onActiveToggle());
  };

  return (
    <div className="SubGoalField d-flex flex-column justify-content-center mt-2">
      <div className="SubGoalField__title">Choose goal to link</div>
      <div className="d-flex align-items-sm-center flex-column flex-sm-row flex-wrap">
        <Select
          isClearable
          id="add-subgoal-select"
          className="mb-2"
          value={option}
          options={options}
          styles={styles}
          onChange={handleSelectChange}
          components={{
            Option: SubGoalFieldOption,
            DropdownIndicator: () => <WpIcon name="search" />,
            SingleValue: SubGoalSingleValue,
            IndicatorSeparator: null,
          }}
          isLoading={isFetching}
          menuPortalTarget={document.body}
        />
        <div className="SubGoalField__buttons mb-2">
          <WpButtonGroup spacing="sm">
            <WpButton
              qaTest="save-subgoal-button"
              disabled={!option?.slug}
              loading={isLoading}
              onClick={handleSaveClick}
              square
              slimPadding
            >
              Save
            </WpButton>
            <WpButton variant="tertiary" onClick={handleCancelClick} square slimPadding>
              Cancel
            </WpButton>
          </WpButtonGroup>
        </div>
        {confirmAddSubGoalModal.isOpen && (
          <ConfirmAddSubGoalModal
            goalId={goalId}
            chosenGoalSlug={confirmAddSubGoalModal.goalSlug}
            onToggle={handleConfirmModalClose}
            onSubmit={handleConfirmModalSubmit}
          />
        )}
      </div>
    </div>
  );
}

export default SubGoalField;
